/**
 * AcceptButton — 4-state button for accepting a coding suggestion.
 * States: idle → writing → success | error
 *
 * Debounced to prevent double-submit.
 */
import { useState, useCallback, useRef } from 'react';
import clsx from 'clsx';
import type { AcceptButtonState, SuggestionStatus, EHRTheme, GapType } from '../types';
import { useSession } from '../context/SessionContext';

interface Props {
  status: SuggestionStatus;
  gapType: GapType;
  isLoading?: boolean;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
  onEdit?: () => void;
  onUndo?: () => void;
  theme?: EHRTheme;
}

const GAP_BADGE_META: Record<GapType, { label: string; bg: string; color: string }> = {
  suspect_new: { label: 'Suspecting Code Gap', bg: '#DBEAFE', color: '#1E40AF' },
  opportunity: { label: 'Recaptured Opportunity', bg: '#DCFCE7', color: '#15803D' },
  recapture: { label: 'Recaptured Existing', bg: '#EDE9FE', color: '#6D28D9' },
  recapture_stale: { label: 'Suspecting Emerging', bg: '#FEF3C7', color: '#B45309' },
  over_claimed: { label: 'Recaptured Risk', bg: '#FEE2E2', color: '#B91C1C' },
};

export function AcceptButton({ status, gapType, isLoading = false, onAccept, onReject, onEdit, onUndo }: Props) {
  const { launch } = useSession();
  const [btnState, setBtnState] = useState<AcceptButtonState>('idle');
  const debounceRef = useRef(false);

  const handleAccept = useCallback(async () => {
    if (debounceRef.current || btnState !== 'idle') return;
    debounceRef.current = true;
    setBtnState('writing');

    try {
      await onAccept();
      setBtnState('success');
      setTimeout(() => setBtnState('idle'), 3000);
    } catch {
      setBtnState('error');
      setTimeout(() => setBtnState('idle'), 4000);
    } finally {
      debounceRef.current = false;
    }
  }, [btnState, onAccept]);

  // Theme-aware primary color
  const primaryBg =
    launch.theme === 'ecw'
      ? 'bg-ecw-teal hover:bg-ecw-teal-dark focus:ring-ecw-teal'
      : 'bg-cerner-blue hover:bg-cerner-blue-dark focus:ring-cerner-blue';

  // ── Already actioned ─────────────────────────────────────────────────────
  const gapBadge = GAP_BADGE_META[gapType];

  if (status === 'accepted') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: gapBadge.bg, color: gapBadge.color }}>{gapBadge.label}</span>
        <div className="flex items-center gap-1 text-emerald-700 text-xs font-semibold">
          <CheckIcon className="w-4 h-4" />
          Accepted
        </div>
        {(onEdit || onUndo) && (
          <div className="flex items-center gap-1 ml-1">
            {onEdit && (
              <button onClick={onEdit} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition" title="Edit">
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {onUndo && (
              <button onClick={onUndo} className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition" title="Undo">
                <UndoIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: gapBadge.bg, color: gapBadge.color }}>{gapBadge.label}</span>
        <div className="flex items-center gap-1 text-red-600 text-xs font-semibold">
          <XIcon className="w-4 h-4" />
          Rejected
        </div>
        {(onEdit || onUndo) && (
          <div className="flex items-center gap-1 ml-1">
            {onEdit && (
              <button onClick={onEdit} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition" title="Edit">
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {onUndo && (
              <button onClick={onUndo} className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition" title="Undo">
                <UndoIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Idle / Writing / Success / Error ─────────────────────────────────────
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        {/* Accept */}
        <button
          onClick={handleAccept}
          disabled={isLoading || btnState !== 'idle'}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-xs font-semibold',
            'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1',
            btnState === 'idle' && `${primaryBg}`,
            btnState === 'writing' && 'bg-gray-400 cursor-wait',
            btnState === 'success' && 'bg-emerald-600',
            btnState === 'error' && 'bg-red-600',
            (isLoading || btnState === 'writing') && 'opacity-80 cursor-not-allowed',
            (btnState === 'success' || btnState === 'error') && 'opacity-80',
          )}
          aria-label="Accept coding suggestion"
        >
          {btnState === 'idle' && (
            <>
              <CheckIcon className="w-3.5 h-3.5" />
              Accept
            </>
          )}
          {btnState === 'writing' && (
            <>
              <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
              Writing...
            </>
          )}
          {btnState === 'success' && (
            <>
              <CheckIcon className="w-3.5 h-3.5" />
              Done
            </>
          )}
          {btnState === 'error' && (
            <>
              <ExclamationIcon className="w-3.5 h-3.5" />
              Failed
            </>
          )}
        </button>

        {/* Edit */}
        {btnState === 'idle' && onEdit && (
          <button
            onClick={onEdit}
            disabled={isLoading}
            className="flex items-center gap-1 px-2 py-1.5 rounded border border-gray-300 text-gray-500 text-xs font-medium hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
            aria-label="Edit coding suggestion before accepting"
            title="Edit before accepting"
          >
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Reject */}
        {btnState === 'idle' && (
          <button
            onClick={onReject}
            disabled={isLoading}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded border border-gray-300 text-gray-600 text-xs font-medium hover:border-red-400 hover:text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300"
            aria-label="Reject coding suggestion"
          >
            <XIcon className="w-3.5 h-3.5" />
            Reject
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Inline icons (no external dep) ──────────────────────────────────────────

function CheckIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function PencilIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
}

function UndoIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" />
    </svg>
  );
}

function ExclamationIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}
